import React, { useEffect, useState } from 'react'
import { AxiosResponse } from 'axios'
import { makeStyles } from '@material-ui/core/styles'
import { Heading, Title, Text } from '@siftscience/focus-components/text'
import { Expand } from '@siftscience/focus-components/icons/Expand'
import { Refresh } from '@siftscience/focus-components/icons/Refresh'
import { Delete } from '@siftscience/focus-components/icons/Delete'
import { NewTab } from '@siftscience/focus-components/icons/NewTab'
import { Button, IconButton } from '@siftscience/focus-components/button'
import { MultiSelect } from '@siftscience/focus-components/select'
import { TextArea } from '@siftscience/focus-components/input'
import axios from '../../../utils/http/axios-local'
import { useMerchantContext } from '../../template-tooling/context/merchant-context'
import { ImageCardProps } from '../types'
import Loader from '../../DET-edit-additions/loader'

const useStyles = makeStyles(() => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    width: '819px',
    position: 'fixed',
    right: 0,
    top: '148px',
    height: '100%',
    background: 'white'
  },
  subContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    maxHeight: 'calc(100vh - 148px - 34px)',
    overflowY: 'auto'
  },
  header: {
    borderBottom: '1px solid rgba(224, 224, 224, 1)',
    height: '72px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    padding: '16px'
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '8px'
  },
  replaceButton: {
    display: 'flex',
    alignItems: 'center'
  },
  flex: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    marginLeft: '16px'
  },
  body: {
    marginTop: '16px',
    padding: '0 24px'
  },
  imageBox: {
    width: '100%',
    height: '336px',
    backgroundColor: 'rgba(247, 247, 247, 1)',
    borderRadius: '8px'
  },
  multiselectLength: {
    width: '15px',
    height: '15px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    backgroundColor: 'black',
    color: 'white'
  },
  multiselectValue: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
  },
  multiselect: {
    width: '100%',
    '& button': {
      width: '100%',
      display: 'flex'
    }
  },
  multiselectMenuItem: {
    width: '100%',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center'
  },
  withLabel: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    marginTop: '16px',
    width: '100%'
  },
  infoBlock: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '16px',
    width: '100%',
    marginTop: '16px'
  },
  template: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderRadius: '4px',
    backgroundColor: 'rgba(247, 247, 247, 1)',
    height: '32px',
    width: '100%',
    padding: '6px 8px'
  },
  templateIcon: {
    cursor: 'pointer'
  },
  img: {
    width: '100%',
    height: '100%',
    objectFit: 'contain'
  }
}))

interface DetailsSidebarProps {
  onClose: () => void
  currentId: string
  allMids: { value: string; label: string }[]
  onDelete: (id: string) => void
  onUpdateEvidence: (
    evidenceId: number | string,
    formData: FormData
  ) => Promise<AxiosResponse>
  onEvidenceChange: (id: string) => void
}

const DetailsSidebar = ({
  onClose,
  currentId,
  onDelete,
  onUpdateEvidence,
  onEvidenceChange,
  allMids
}: DetailsSidebarProps) => {
  const classes = useStyles()
  const { selectedMerchant } = useMerchantContext()
  const [evidenceData, setEvidenceData] = useState<ImageCardProps>(null)
  const [loading, setLoading] = useState(false)

  const fetchEvidence = async () => {
    setLoading(true)
    try {
      const response = await axios.get(
        `/det/tooling/merchant_library/${currentId}`,
        {
          params: {
            merchant_id: selectedMerchant.id
          }
        }
      )
      setEvidenceData({
        ...response.data.evidence,
        imagePath: response.data.evidence.image_path,
        mids: response.data.evidence.mid_ids
      })
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const onUpdateEvidenceData = async (formData: FormData) => {
    setLoading(true)
    await onUpdateEvidence(evidenceData.id, formData)
    await fetchEvidence()
    setLoading(false)
  }

  const onMidsChange = async (newMids: { value: number }[]) => {
    const formData = new FormData()

    if (newMids.length === 0) {
      formData.append('evidence[mid_ids][]', '')
    }

    newMids.forEach(item => {
      formData.append('evidence[mid_ids][]', `${item.value}`)
    })

    await onUpdateEvidenceData(formData)
  }

  const onDescriptionSave = async (
    event: React.FocusEvent<HTMLTextAreaElement>
  ) => {
    const value = event?.target?.value || ''

    const formData = new FormData()
    formData.append('evidence[caption]', value)
    await onUpdateEvidenceData(formData)
  }

  useEffect(() => {
    void fetchEvidence()
  }, [])

  if (loading && !evidenceData) {
    return <Loader />
  }

  if (!evidenceData) {
    return null
  }

  return (
    <div className={`shadow-l ${classes.container}`}>
      <div className={classes.subContainer}>
        {loading && !!evidenceData && <Loader />}
        <div className={classes.header}>
          <IconButton onClick={onClose} size="large" variant="secondary-ghost">
            <Expand />
          </IconButton>
          <div className={classes.flex}>
            <Heading size={4}>{evidenceData.group}</Heading>
            <div className={classes.actions}>
              <Button
                onClick={() => onEvidenceChange(currentId)}
                className={classes.replaceButton}
                size="medium"
                variant={'secondary'}
              >
                <Refresh />
                <Title size="xsmall">Replace</Title>
              </Button>
              <IconButton
                onClick={() => onDelete(currentId)}
                size="medium"
                variant={'secondary'}
              >
                <Delete />
              </IconButton>
            </div>
          </div>
        </div>
        <div className={classes.body}>
          <div className={classes.imageBox}>
            {evidenceData.imagePath && (
              <img
                className={classes.img}
                src={evidenceData.imagePath}
                alt="img"
              />
            )}
          </div>
          {evidenceData?.mids && (
            <div className={classes.withLabel}>
              <Title color={'secondary'} size="small">
                MIDs
              </Title>
              <MultiSelect
                className={classes.multiselect}
                selectedItems={allMids.filter(item =>
                  evidenceData.mids.includes(item.value)
                )}
                variant={'secondary'}
                items={allMids}
                selectedItemsRenderer={(items: { label: string }[]) => {
                  const MAX_ITEMS = 5
                  const labels = items.map(item => item.label)
                  const displayText =
                    labels.length > MAX_ITEMS
                      ? `${labels.slice(0, MAX_ITEMS).join(', ')}...`
                      : labels.join(', ')

                  return items?.length > 0 ? (
                    <div className={classes.multiselectValue}>
                      <Text className={classes.multiselectLength} size="small">
                        {items.length}
                      </Text>{' '}
                      <Text size="small">{displayText}</Text>
                    </div>
                  ) : (
                    ''
                  )
                }}
                onChange={onMidsChange}
                stretch
              />
            </div>
          )}

          <div className={classes.infoBlock}>
            <div className={classes.withLabel}>
              <Title color={'secondary'} size="small">
                Used in {evidenceData.templates.length} templates
              </Title>
              {evidenceData.templates.map(template => (
                <div key={template.id} className={classes.template}>
                  <Text color={'primary'} size="small">
                    {template.name}
                  </Text>
                  {/* <NewTab className={classes.templateIcon} /> */}
                </div>
              ))}
            </div>
            <div className={classes.withLabel}>
              <Title color={'secondary'} size="small">
                Description
              </Title>
              <TextArea
                defaultValue={evidenceData?.caption || ''}
                onBlur={onDescriptionSave}
                placeholder="Add description"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DetailsSidebar
